import { SanitationEquipment, SanitationRecord } from '../types/sanitationRecord';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

const sanitationApiService = {
  // Equipment endpoints
  fetchEquipment: async (): Promise<SanitationEquipment[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/sanitation/equipment`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch sanitation equipment');
      }

      return data.equipment;
    } catch (error) {
      console.error('Error fetching sanitation equipment:', error);
      throw error;
    }
  },

  createEquipment: async (equipment: Partial<SanitationEquipment>): Promise<SanitationEquipment> => {
    try {
      const response = await fetch(`${API_BASE_URL}/sanitation/equipment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(equipment),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create sanitation equipment');
      }

      return data.equipment;
    } catch (error) {
      console.error('Error creating sanitation equipment:', error);
      throw error;
    }
  },

  updateEquipment: async (id: string, equipment: Partial<SanitationEquipment>): Promise<SanitationEquipment> => {
    try {
      const response = await fetch(`${API_BASE_URL}/sanitation/equipment/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(equipment),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update sanitation equipment');
      }

      return data.equipment;
    } catch (error) {
      console.error('Error updating sanitation equipment:', error);
      throw error;
    }
  },

  deleteEquipment: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/sanitation/equipment/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete sanitation equipment');
      }
    } catch (error) {
      console.error('Error deleting sanitation equipment:', error);
      throw error;
    }
  },

  // Record endpoints
  fetchRecords: async (equipmentId?: string): Promise<SanitationRecord[]> => {
    try {
      const url = equipmentId
        ? `${API_BASE_URL}/sanitation/records?equipmentId=${equipmentId}`
        : `${API_BASE_URL}/sanitation/records`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch sanitation records');
      }

      return data.records;
    } catch (error) {
      console.error('Error fetching sanitation records:', error);
      throw error;
    }
  },

  createRecord: async (record: Partial<SanitationRecord>): Promise<SanitationRecord> => {
    try {
      const response = await fetch(`${API_BASE_URL}/sanitation/records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(record),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create sanitation record');
      }

      return data.record;
    } catch (error) {
      console.error('Error creating sanitation record:', error);
      throw error;
    }
  },

  updateRecord: async (id: string, record: Partial<SanitationRecord>): Promise<SanitationRecord> => {
    try {
      const response = await fetch(`${API_BASE_URL}/sanitation/records/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(record),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update sanitation record');
      }

      return data.record;
    } catch (error) {
      console.error('Error updating sanitation record:', error);
      throw error;
    }
  },

  deleteRecord: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/sanitation/records/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete sanitation record');
      }
    } catch (error) {
      console.error('Error deleting sanitation record:', error);
      throw error;
    }
  },
};

export default sanitationApiService;
